import { useEffect, useState } from "react";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { isWebMode, prettifyApiError } from "@/shared/api";
import {
  type AllServiceSettings,
  saveCredential,
  updateAllServiceSettings,
  uploadAllServiceTemplate,
  useResource,
} from "./api";
import { Pillola, TASTO } from "./parti";

type Props = {
  onCredentialsChanged?: () => void;
};

const THUNDERBIRD_PLACEHOLDER = "C:\\Program Files\\Mozilla Thunderbird\\thunderbird.exe";

export function AllServiceSettingsCard({ onCredentialsChanged }: Props) {
  const settings = useResource<AllServiceSettings>("/api/suppliers/allservice/settings", {
    template_path: "",
    template_configured: false,
    template_exists: false,
    template_blob_present: false,
    template_blob_filename: "",
    thunderbird_path: "",
    thunderbird_configured: false,
    thunderbird_exists: false,
  });
  const [templatePath, setTemplatePath] = useState("");
  const [thunderbirdPath, setThunderbirdPath] = useState("");
  // Audit 2026-05-11 M10: ``dirty`` impedisce che il poll/re-fetch di
  // ``settings.value.template_path`` riscriva l'input mentre l'utente sta
  // ancora digitando. Stessa logica applicata al path Thunderbird.
  const [dirty, setDirty] = useState(false);
  const [tbDirty, setTbDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (dirty) return;
    setTemplatePath(settings.value.template_path);
  }, [settings.value.template_path, dirty]);

  useEffect(() => {
    if (tbDirty) return;
    setThunderbirdPath(settings.value.thunderbird_path);
  }, [settings.value.thunderbird_path, tbDirty]);

  function updateTemplatePath(value: string) {
    setDirty(true);
    setTemplatePath(value);
  }

  function updateThunderbirdPath(value: string) {
    setTbDirty(true);
    setThunderbirdPath(value);
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await updateAllServiceSettings(templatePath);
      // Path TB salvato in keyring SOLO se l'utente lo ha modificato.
      // Lasciare vuoto = usa il default Windows (gestito server-side
      // da resolve_thunderbird_path).
      const tbValue = thunderbirdPath.trim();
      if (tbDirty && tbValue) {
        await saveCredential({
          key: "thunderbird_path",
          label: "Thunderbird — eseguibile",
          value: tbValue,
        });
      }
      setMessage("Impostazioni AllService salvate.");
      setDirty(false);
      setTbDirty(false);
      await settings.refresh();
      onCredentialsChanged?.();
    } catch (err) {
      setMessage(null);
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function uploadTemplate(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Reset input così ricaricare lo stesso file ri-triggera l'onChange.
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    setError(null);
    setMessage(null);
    try {
      await uploadAllServiceTemplate(file);
      setMessage(`Template "${file.name}" caricato nel database.`);
      await settings.refresh();
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setUploading(false);
    }
  }

  // "Pronto" = file locale presente (desktop) OPPURE template caricato nel DB (web).
  const templateReady = settings.value.template_exists || settings.value.template_blob_present;
  const tbExists = settings.value.thunderbird_exists;
  const tbStatusLabel = tbExists
    ? settings.value.thunderbird_configured
      ? "Configurato"
      : "Default Windows"
    : "Non trovato";
  const tbStatusClass = tbExists ? "bg-green-soft text-ink" : "bg-amber-soft text-ink";

  return (
    <div className="px-5 py-4 max-md:px-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-bold text-ink max-md:text-[17px]">
            AllService · modello Excel
          </h3>
          <p className="mt-0.5 text-[12.5px] text-ink-2 max-md:text-[14px] max-md:text-ink">
            Il file MASTER da cui OM crea l'Excel di ogni ordine. La mail parte dal server, da sola
            o col tasto «Invia ordine AS».
          </p>
          {/* Il pulsante dell'invio automatico vive negli automatismi (una
              lane come le altre): senza questo rimando lo si cerca qui. */}
          <p className="mt-1 text-[12.5px] text-ink-2 max-md:text-[14px] max-md:text-ink">
            L'invio automatico si accende in{" "}
            <a
              href="#lane-as_invio"
              className="font-semibold text-ink underline underline-offset-2"
            >
              Automatismi → AllService · invio ordini
            </a>
            .
          </p>
        </div>
        <Pillola tono={templateReady ? "ok" : "attenzione"}>
          {templateReady ? "Pronto" : "Da configurare"}
        </Pillola>
      </div>

      <form onSubmit={(event) => void submit(event)} className="space-y-3">
        {/* Percorso sul filesystem della macchina che ESEGUE il backend:
            ``template_exists`` e' verificato lato server, quindi sul web un
            path del PC non potra' mai esistere. Lasciarlo per primo faceva
            compilare e salvare un campo inutile, col badge fermo su "Da
            configurare": la via valida e' il riquadro di upload qui sotto.
            Il valore resta salvato — ``templatePath`` parte da
            ``settings.value.template_path`` e il submit lo rimanda invariato. */}
        {!isWebMode() && (
          <label className="block text-[12px] font-medium text-ink-2">
            Template XLSX — percorso locale (solo sviluppo, su questa macchina)
            <input
              type="text"
              value={templatePath}
              onChange={(event) => updateTemplatePath(event.target.value)}
              className="mt-1 w-full rounded-lg border border-line bg-paper px-3 py-2 tabular-nums text-[12px] focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-soft"
            />
          </label>
        )}

        <div className="rounded-lg border border-line bg-paper/60 p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[13px] font-bold text-ink">Modello MASTER</span>
            {settings.value.template_blob_present ? (
              <small className="rounded-full bg-green-soft px-2 py-0.5 text-[12px] font-bold text-ink">
                Caricato
              </small>
            ) : (
              <small className="rounded-full bg-amber-soft px-2 py-0.5 text-[12px] font-bold text-ink">
                Assente
              </small>
            )}
          </div>
          {settings.value.template_blob_present && (
            <p className="mt-1 truncate tabular-nums text-[11px] text-ink-2">
              {settings.value.template_blob_filename}
            </p>
          )}
          <p className="mt-1.5 text-[11px] text-ink-2">
            Caricalo di nuovo ogni volta che AllService cambia il modello.
          </p>
          <label className={`mt-2 cursor-pointer ${TASTO}`}>
            <input
              type="file"
              accept=".xlsx,.xlsm"
              onChange={(event) => void uploadTemplate(event)}
              disabled={uploading}
              className="hidden"
            />
            {uploading ? "Carico…" : "Carica il modello"}
          </label>
        </div>

        {/* Thunderbird serve solo in sviluppo (OM_MAIL_BACKEND=thunderbird):
            sul server la mail parte via SMTP e il campo e' ignorato. */}
        {!isWebMode() && (
          <label className="block text-[12px] font-medium text-ink-2">
            <span className="flex items-center justify-between gap-2">
              Thunderbird — percorso eseguibile
              <small
                className={["rounded-full px-2 py-0.5 text-[12px] font-bold", tbStatusClass].join(
                  " ",
                )}
              >
                {tbStatusLabel}
              </small>
            </span>
            <input
              type="text"
              value={thunderbirdPath}
              onChange={(event) => updateThunderbirdPath(event.target.value)}
              placeholder={THUNDERBIRD_PLACEHOLDER}
              className="mt-1 w-full rounded-lg border border-line bg-paper px-3 py-2 tabular-nums text-[12px] focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent-soft"
            />
            <small className="mt-1 block text-[11px] text-ink-2">
              Serve solo se l'invio passa da Thunderbird (OM_MAIL_BACKEND=thunderbird, sviluppo su
              Windows): l'app apre Thunderbird con l'email pronta e gli allegati, clicchi "Invia"
              tu. Sul server la mail parte via SMTP e questo campo è ignorato.
            </small>
          </label>
        )}

        <ErrorBanner message={settings.error} />
        <ErrorBanner message={error} />
        {message && !error && (
          <div className="rounded-full border border-green-soft bg-green-soft px-3 py-2 text-[12.5px] text-ink">
            {message}
          </div>
        )}

        {!isWebMode() && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={busy}
              className="rounded-full border border-accent bg-accent-2 px-3 py-1.5 text-[12px] font-medium text-white disabled:opacity-50"
            >
              {busy ? "Salvataggio..." : "Salva AllService"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
